import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Checkbox, Input, Modal } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getLoginInProgress, loginUser } from '../../features/auth.store';

import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import to from '../../utils/to';
import eng from '../../utils/eng';
import history from '../../utils/history';
import PublicPageLayout from './public-page-layout';
import { POST } from '../../service/base-api-service';

const Login = () => {
  const dispatch = useDispatch();
  const [certificationReminder, setCertificationReminder] = useState('');
  const [isRenewalInProgress, setIsRenewalInProgress] = useState(false);
  const renewalInProgress = useRef(false);

  const proceedToDashboard = () => {
    history.push('/dashboard/insights');
    toast.success(eng.toast.loginSuccess);
  };

  const handleCertificationRenewal = async () => {
    if (renewalInProgress.current) return;

    renewalInProgress.current = true;
    setIsRenewalInProgress(true);
    try {
      await POST('v1/users/certification/renew', {});
      console.log('Certification renewal API succeeded: email sent to MineARC Service Team.');
      toast.success('Email sent to MineARC Service Team');
      setCertificationReminder('');
      proceedToDashboard();
    } catch (error) {
      toast.error(error?.message || 'Unable to send the renewal request. Please try again.');
    } finally {
      renewalInProgress.current = false;
      setIsRenewalInProgress(false);
    }
  };

  const getCertificationErrorMessage = (error) => {
    const details = error?.details || error?.data?.details;
    if (Array.isArray(details) && details.length > 0) return String(details[0]);
    return error?.message || error?.data?.message || 'Your certification has expired. Please renew it to regain access.';
  };

  const getCertificationReminderMeta = (message) => {
    const warningText = String(message || '').trim();
    const daysMatch = warningText.match(/expires in\s+(\d+)\s+days?/i);
    const dateMatch = warningText.match(/on\s+(\d{4}-\d{2}-\d{2})/i);
    const daysUntilExpiry = daysMatch ? Number(daysMatch[1]) : null;
    const rawExpiryDate = dateMatch ? dateMatch[1] : '';
    const parsedExpiryDate = rawExpiryDate ? new Date(`${rawExpiryDate}T00:00:00`) : null;
    const expiryDate = parsedExpiryDate && !Number.isNaN(parsedExpiryDate.getTime())
      ? parsedExpiryDate.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : rawExpiryDate || 'the upcoming renewal date';

    return {
      isUpcoming: !/expired/i.test(warningText) && (daysUntilExpiry === null || daysUntilExpiry >= 0),
      daysUntilExpiry,
      expiryDate,
    };
  };

  const handleLogin = async (values) => {
    const [err, result] = await to(dispatch(loginUser(values)).unwrap());
    if (err) {
      const errorCode = err?.errorCode ?? err?.data?.errorCode;

      if (errorCode === 21001) {
        setCertificationReminder(getCertificationErrorMessage(err));
        return;
      }

      return toast.error(err.status === 401 ? eng.toast.invalidUsernamePassword : err.message);
    }

    if (result?.certificationExpiryWarning) {
      setCertificationReminder(result.certificationExpiryWarning);
      return;
    }

    proceedToDashboard();
  };

  const isLoginInProgress = useSelector(getLoginInProgress);
  const reminderMeta = getCertificationReminderMeta(certificationReminder);

  return (
    <PublicPageLayout>
      <div className="title fw-700">Hello! </div>
      <>
          <div className="sub-title fw-400">Sign in to continue!</div>

          <Formik
            initialValues={{ email: '', password: '', rememberMe: true }}
            onSubmit={handleLogin}
            className="d-flex f-col"
            validateOnBlur
            validationSchema={Yup.object({
              email: Yup.string().email('Invalid email address').required('Required'),
              password: Yup.string().required('Required'),
            })}
          >
            <Form>
              <div className="login-field">
                <label htmlFor="email">Email ID</label>
                <Field name="email">
                  {({ field, meta }) => (
                    <Input
                      placeholder="Enter your email"
                      className={`${meta.touched && meta.error ? 'has-error' : ''}`}
                      suffix={meta.touched ? meta.error ? <CloseOutlined /> : <CheckOutlined /> : <span />}
                      {...field}
                    />
                  )}
                </Field>
              </div>
              <div className="login-field">
                <label htmlFor="password">Password</label>
                <Field name="password">
                  {({ field, meta }) => (
                    <Input.Password
                      placeholder="Enter your password"
                      className={`${meta.touched && meta.error ? 'has-error' : ''}`}
                      {...field}
                    />
                  )}
                </Field>
              </div>

              <div className="remember-me-forgot-password d-flex jc-space-between">
                <Field as={Checkbox} type="checkbox" name="rememberMe">
                  Remember Me
                </Field>
                <Link to="/forgot-password">Forgot Password ?</Link>
              </div>

              <Button disabled={isLoginInProgress} className="upper-case" type="primary" size="large" htmlType="submit">
                Login to continue
              </Button>
            </Form>
          </Formik>
      </>

      <Modal
        title={null}
        visible={Boolean(certificationReminder)}
        maskClosable={false}
        closable={false}
        footer={null}
        centered
        wrapClassName="certificate-expiry-modal-wrap"
      >
        <div className="certificate-reminder-card">
          <div className="certificate-reminder-hero">
            <div className="certificate-reminder-brand">
              <img src="/logo.svg" alt="MineARC logo" className="certificate-reminder-logo" />
              <div>
                <div className="certificate-reminder-kicker">MineARC Systems</div>
                <h2>
                  {reminderMeta.isUpcoming ? 'Certification Renewal Reminder' : 'Certification Renewal Required Now'}
                </h2>
              </div>
            </div>
            {reminderMeta.isUpcoming && (
              <div className="certificate-reminder-pill">Upcoming Renewal Required</div>
            )}
          </div>

          <div className="certificate-reminder-body">
            {reminderMeta.isUpcoming ? (
              <p className="certificate-reminder-message">
                Your MineARC Refuge Chamber Service and Maintenance Certification expires in{' '}
                <strong>{reminderMeta.daysUntilExpiry ?? 'a few'} days</strong> on{' '}
                <strong>{reminderMeta.expiryDate}</strong>.
              </p>
            ) : (
              <p className="certificate-reminder-message">
                Your MineARC Refuge Chamber Service and Maintenance Certification has now expired.
              </p>
            )}

            <p className="certificate-reminder-message">
              During recertification, we ensure you are kept up to date with any chamber upgrades and technical advancements.
            </p>

            <div className="certificate-reminder-support">
              Please contact your local <strong>MineARC Service Team</strong> to arrange renewal
              {reminderMeta.isUpcoming ? '.' : ' as soon as possible.'}
            </div>
          </div>

          <div className="certificate-reminder-actions">
            <Button
              size="large"
              className="upper-case"
              loading={isRenewalInProgress}
              disabled={isRenewalInProgress}
              onClick={handleCertificationRenewal}
            >
              Arrange Recertification Now
            </Button>
            <Button
              type="primary"
              size="large"
              className="upper-case"
              disabled={isRenewalInProgress}
              onClick={() => {
                setCertificationReminder('');
                proceedToDashboard();
              }}
            >
              OK, CONTINUE TO APP
            </Button>
          </div>
        </div>
      </Modal>
    </PublicPageLayout>
  );
};

export default Login;
